import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Stepper, Step, StepLabel, StepButton } from '@material-ui/core';
import { IoIosArrowBack } from 'react-icons/io';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import ativarEmissaoService, {
  EmpresaFiscal,
  getMissingFiscalFields,
} from '../../services/ativarEmissaoService';

import Step1DadosFiscais from './components/Step1DadosFiscais';
import Step2Certificado from './components/Step2Certificado';
import Step3Csc from './components/Step3Csc';
import Step4AtivarAmbiente from './components/Step4AtivarAmbiente';
import * as S from './styles';

const STEP_LABELS = ['Dados fiscais', 'Certificado', 'CSC', 'Ativar ambiente'];

/**
 * Tela "Ativar emissão" (Story 1-7 / T1.29 — AC9/AC10).
 *
 * Wizard de 4 passos que ativa a emissão fiscal de uma empresa de ponta a ponta:
 * valida os dados fiscais (1-4), provisiona tenant+key (1-6 via PATCH .../fiscal),
 * envia o certificado A1 e configura o CSC — refletindo o status em cada etapa.
 *
 * Ordem técnica (Dúvida D5): certificado e CSC exigem X-API-Key (tenant+key), que
 * só existem após o provisionamento. Por isso, ao avançar do passo 1, disparamos
 * o PATCH .../fiscal (provisionamento) de forma transparente — mantendo a ordem
 * visual "dados → certificado → CSC → ativar".
 */
const AtivarEmissao: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();
  const empresaId = user.empresa_id;

  // Saída da tela (o "Voltar" das ações navega entre passos, não sai do wizard).
  // Preferimos goBack para cair na aba Fiscal de onde o usuário veio: a rota
  // /configuracoes/:tab não abre a aba Fiscal por URL (o TabBar limita :tab a 9
  // e cai no índice 10), então o fallback só garante sair do wizard.
  const handleLeave = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push('/configuracoes/0');
  }, [history]);

  const [company, setCompany] = useState<EmpresaFiscal | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async (): Promise<EmpresaFiscal | null> => {
    try {
      const data = await ativarEmissaoService.getCompany(empresaId);
      setCompany(data);
      return data;
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao carregar dados da empresa',
      });
      return null;
    }
  }, [empresaId, addToast]);

  // Reentrada com a emissão já habilitada: abre direto no passo 4, onde ficam o
  // ambiente e o desligamento. Senão o usuário teria que passar pelo "Habilitar
  // e avançar" (que re-habilita) só para alcançar o botão de desabilitar.
  // O `saltouParaAtivo` garante que isso só vale na primeira carga — depois o
  // passo é do usuário.
  // (ref, não state: mudar isso não deve re-disparar o efeito e refazer o GET)
  const saltouParaAtivo = useRef(false);

  useEffect(() => {
    reload().then((data) => {
      if (data?.emissao_habilitada && !saltouParaAtivo.current) {
        saltouParaAtivo.current = true;
        setActiveStep(STEP_LABELS.length - 1);
      }
    });
  }, [reload]);

  const missing = getMissingFiscalFields(company);

  const handleError = useCallback(
    (err: any, fallback: string) => {
      const message =
        err?.response?.data?.message || err?.message || fallback;
      addToast({ type: 'error', title: fallback, description: message });
    },
    [addToast],
  );

  // Passo 1 → 2: provisiona (tenant+key) de forma transparente (D5).
  const handleNextFromFiscal = useCallback(async () => {
    if (missing.length > 0) {
      addToast({
        type: 'error',
        title: 'Complete os dados fiscais',
        description: `Pendências: ${missing.join(', ')}`,
      });
      return;
    }

    setLoading(true);
    try {
      await ativarEmissaoService.patchFiscal(empresaId, {
        emissao_habilitada: true,
        ambiente_fiscal: company?.ambiente_fiscal || 2,
      });
      await reload();
      addToast({ type: 'success', title: 'Empresa provisionada na atac-notas' });
      setActiveStep(1);
    } catch (err) {
      handleError(err, 'Falha ao provisionar a empresa');
    } finally {
      setLoading(false);
    }
  }, [missing, empresaId, company, reload, addToast, handleError]);

  const handleCertificado = useCallback(
    async (file: File, password: string) => {
      setLoading(true);
      try {
        await ativarEmissaoService.postCertificado(empresaId, file, password);
        await reload();
        addToast({ type: 'success', title: 'Certificado enviado com sucesso' });
        setActiveStep(2);
      } catch (err) {
        handleError(err, 'Falha ao enviar o certificado');
      } finally {
        setLoading(false);
      }
    },
    [empresaId, reload, addToast, handleError],
  );

  const handleCsc = useCallback(
    async (payload: { csc: string; idCsc: string; ambiente: number }) => {
      setLoading(true);
      try {
        await ativarEmissaoService.postCsc(empresaId, payload);
        await reload();
        addToast({ type: 'success', title: 'CSC configurado com sucesso' });
        setActiveStep(3);
      } catch (err) {
        handleError(err, 'Falha ao configurar o CSC');
      } finally {
        setLoading(false);
      }
    },
    [empresaId, reload, addToast, handleError],
  );

  const handleAtivar = useCallback(
    async (ambiente: number) => {
      setLoading(true);
      try {
        await ativarEmissaoService.patchFiscal(empresaId, {
          emissao_habilitada: true,
          ambiente_fiscal: ambiente,
        });
        await reload();
        addToast({ type: 'success', title: 'Emissão ativada com sucesso' });
      } catch (err) {
        handleError(err, 'Falha ao ativar a emissão');
      } finally {
        setLoading(false);
      }
    },
    [empresaId, reload, addToast, handleError],
  );

  // Desliga a emissão da empresa. Mantém o ambiente fiscal atual: o PATCH exige
  // ambiente_fiscal e mandar outro valor mudaria produção/homologação de carona.
  const handleDesativar = useCallback(async () => {
    setLoading(true);
    try {
      await ativarEmissaoService.patchFiscal(empresaId, {
        emissao_habilitada: false,
        ambiente_fiscal: company?.ambiente_fiscal || 2,
      });
      await reload();
      addToast({
        type: 'success',
        title: 'Emissão desabilitada',
        description: 'O PDV deixa de emitir NFC-e para esta empresa.',
      });
    } catch (err) {
      handleError(err, 'Falha ao desabilitar a emissão');
    } finally {
      setLoading(false);
    }
  }, [empresaId, company, reload, addToast, handleError]);

  // AC9: só permite avançar via "Próximo" se o passo atual já está concluído
  // (reentrada). Certificado/CSC pendentes exigem concluir o passo, não pular.
  const stepConcluded = (step: number): boolean => {
    if (step === 1) return company?.certificado_status === 'valido';
    // O CSC deixou de travar o avanço: é possível ativar sem ele e configurar
    // depois (o passo 3 avisa que a SEFAZ rejeita a nota enquanto faltar).
    if (step === 2) return true;
    return false;
  };

  // Reentrada com a emissão já ativa: a tela vira painel de manutenção, não
  // wizard. Os dois "Voltar" (cabeçalho x rodapé) confundiam — aqui o rodapé
  // deixa de navegar e a troca de passo passa a ser pelo próprio Stepper.
  const jaHabilitada = !!company?.emissao_habilitada;

  const renderStep = (): React.ReactNode => {
    switch (activeStep) {
      case 0:
        return <Step1DadosFiscais company={company} missing={missing} />;
      case 1:
        return (
          <Step2Certificado
            company={company}
            loading={loading}
            onSubmit={handleCertificado}
          />
        );
      case 2:
        return (
          <Step3Csc
            company={company}
            loading={loading}
            onSubmit={handleCsc}
            onSkip={() => setActiveStep(3)}
          />
        );
      case 3:
        return (
          <Step4AtivarAmbiente
            company={company}
            loading={loading}
            onActivate={handleAtivar}
            onDeactivate={handleDesativar}
          />
        );
      default:
        return null;
    }
  };

  const tone = (
    ok: boolean | undefined,
    status?: string | null,
  ): 'ok' | 'warn' | 'error' => {
    if (ok || status === 'valido' || status === 'configurado') return 'ok';
    if (status === 'invalido' || status === 'erro' || status === 'expirado') {
      return 'error';
    }
    return 'warn';
  };

  return (
    <S.Container>
      <div className="header">
        <button className="previous_page" type="button" onClick={handleLeave}>
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <h2>Ativar emissão</h2>
        <p>
          Configure o certificado, o CSC e ative o ambiente fiscal da empresa
          para emitir NFC-e.
        </p>
      </div>

      <Stepper activeStep={activeStep} alternativeLabel nonLinear={jaHabilitada}>
        {STEP_LABELS.map((label, index) => (
          <Step key={label}>
            {jaHabilitada ? (
              <StepButton onClick={() => setActiveStep(index)}>
                {label}
              </StepButton>
            ) : (
              <StepLabel>{label}</StepLabel>
            )}
          </Step>
        ))}
      </Stepper>

      {/* Status consolidado (AC9/AC10) */}
      <S.SummaryGrid>
        <div className="item">
          <span className="label">Emissão</span>
          <S.StatusBadge tone={tone(company?.emissao_habilitada)}>
            {company?.emissao_habilitada ? 'Habilitada' : 'Pendente'}
          </S.StatusBadge>
        </div>
        <div className="item">
          <span className="label">Certificado</span>
          <S.StatusBadge tone={tone(false, company?.certificado_status)}>
            {company?.certificado_status || '—'}
          </S.StatusBadge>
        </div>
        <div className="item">
          <span className="label">CSC</span>
          <S.StatusBadge tone={tone(false, company?.csc_status)}>
            {company?.csc_status || '—'}
          </S.StatusBadge>
        </div>
      </S.SummaryGrid>

      {renderStep()}

      <S.Actions>
        {jaHabilitada ? (
          // Emissão já ativa: um único caminho de saída, sem o "Voltar" do
          // rodapé competindo com o do cabeçalho.
          <S.Button type="button" className="only" onClick={handleLeave}>
            Concluir
          </S.Button>
        ) : (
          <>
            <S.Button
              type="button"
              variant="ghost"
              onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
              disabled={activeStep === 0 || loading}
            >
              Passo anterior
            </S.Button>

            {activeStep === 0 ? (
              <S.Button
                type="button"
                onClick={handleNextFromFiscal}
                disabled={loading || missing.length > 0}
              >
                {loading ? 'Provisionando…' : 'Habilitar e avançar'}
              </S.Button>
            ) : (
              <S.Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setActiveStep((s) => Math.min(STEP_LABELS.length - 1, s + 1))
                }
                disabled={
                  loading ||
                  activeStep === STEP_LABELS.length - 1 ||
                  !stepConcluded(activeStep)
                }
              >
                Próximo
              </S.Button>
            )}
          </>
        )}
      </S.Actions>
    </S.Container>
  );
};

export default AtivarEmissao;
